// Especies de una transferencia de títulos (entrada o salida).
//
// Una misma instrucción puede mover varias especies hacia la misma contraparte.
// El detalle vive en `titulos` ([{ especie, cantidad }]); `especie` y `cantidad`
// quedan como resumen (especies separadas por coma y VN total) para los lectores
// que no conocen la lista: el backup del cierre diario y el orden de la tabla.
//
// Las instrucciones cargadas antes de la lista solo tienen `especie`/`cantidad`:
// `getTitulosInstruccion` las lee como una lista de un elemento, así que ningún
// lector tiene que distinguir entre los dos formatos.

import { formatNumberForDisplay, parseAmountForState } from './formatters';

export const TIPOS_TITULOS = ['Transferencia Titulos Salida', 'Transferencia Titulos Entrada'];

// Tope para que un documento no crezca sin límite; las reglas de Firestore
// aplican el mismo número.
export const MAX_TITULOS_POR_INSTRUCCION = 30;

let tituloRowSeq = 0;

/** Fila editable del formulario. `rowId` solo existe para el `key` de React. */
export const createTituloRow = ({ especie = '', cantidadInputValue = '' } = {}) => {
    tituloRowSeq += 1;
    return { rowId: `titulo-${tituloRowSeq}`, especie, cantidadInputValue };
};

const parseCantidad = (inputValue) => parseFloat(parseAmountForState(String(inputValue ?? ''))) || 0;

const normalizeEspecie = (especie) => String(especie ?? '').trim().toUpperCase();

// Una fila sin especie ni cantidad es la que quedó vacía al agregar: se ignora
// en lugar de hacer fallar la validación.
const isEmptyRow = (row) => !String(row?.especie ?? '').trim() && !String(row?.cantidadInputValue ?? '').trim();

/** Lista de títulos de una instrucción guardada, en el formato nuevo o en el anterior. */
export const getTitulosInstruccion = (instr) => {
    if (Array.isArray(instr?.titulos) && instr.titulos.length > 0) {
        return instr.titulos.map((titulo) => ({
            especie: titulo?.especie || '',
            cantidad: Number(titulo?.cantidad) || 0,
        }));
    }
    if (!instr?.especie && !Number(instr?.cantidad)) return [];
    return [{ especie: instr.especie || '', cantidad: Number(instr.cantidad) || 0 }];
};

/** Filas del formulario para editar una instrucción; siempre al menos una. */
export const titulosToFormRows = (instr) => {
    const titulos = getTitulosInstruccion(instr);
    if (titulos.length === 0) return [createTituloRow()];
    return titulos.map(({ especie, cantidad }) => createTituloRow({
        especie,
        cantidadInputValue: cantidad ? formatNumberForDisplay(cantidad) : '',
    }));
};

/** Lista que se guarda en `titulos`, sin las filas vacías. */
export const buildTitulosPayload = (rows = []) => rows
    .filter((row) => !isEmptyRow(row))
    .map((row) => ({
        especie: normalizeEspecie(row.especie),
        cantidad: parseCantidad(row.cantidadInputValue),
    }));

/** Mensaje de error para el formulario, o null si las filas son válidas. */
export const validateTitulosRows = (rows = []) => {
    const cargadas = rows
        .map((row, index) => ({ row, numero: index + 1 }))
        .filter(({ row }) => !isEmptyRow(row));
    if (cargadas.length === 0) return 'Debe indicar la especie.';
    if (cargadas.length > MAX_TITULOS_POR_INSTRUCCION) {
        return `Una instrucción admite hasta ${MAX_TITULOS_POR_INSTRUCCION} especies.`;
    }

    // Con una sola fila los mensajes son los de siempre; con varias se aclara cuál falla.
    const enFila = (numero, texto) => (
        rows.length > 1 ? `Fila ${numero}: ${texto.charAt(0).toLowerCase()}${texto.slice(1)}` : texto
    );
    const vistas = new Set();
    for (const { row, numero } of cargadas) {
        const especie = normalizeEspecie(row.especie);
        if (!especie) return enFila(numero, 'Debe indicar la especie.');
        if (parseCantidad(row.cantidadInputValue) <= 0) {
            return enFila(numero, 'La cantidad debe ser mayor a cero.');
        }
        if (vistas.has(especie)) {
            return `La especie ${especie} está repetida: cargala en una sola fila con la cantidad total.`;
        }
        vistas.add(especie);
    }
    return null;
};

/** "AL30, GD30": resumen que se guarda en `especie`. */
export const resumirEspecies = (titulos = []) => titulos
    .map((titulo) => titulo.especie)
    .filter(Boolean)
    .join(', ');

/** VN total que se guarda en `cantidad`. */
export const sumarCantidades = (titulos = []) => titulos
    .reduce((total, titulo) => total + (Number(titulo.cantidad) || 0), 0);

/** "AL30: 1.000,00 VN · GD30: 500,00 VN", para textos de una sola línea. */
export const describirTitulos = (titulos = []) => titulos
    .map(({ especie, cantidad }) => `${especie || '-'}: ${formatNumberForDisplay(cantidad)} VN`)
    .join(' · ');
